// Détection du contexte natif (app Capacitor) et résolution de l'origine
// backend/relay.
//
// Sur le web et la PWA, la console est servie par le relay lui-même :
// tout est same-origin (`window.location.origin`). Dans l'app native
// Capacitor, l'origine de la WebView est `capacitor://localhost` — il faut
// alors viser explicitement l'URL absolue du relay.

import { Capacitor } from "@capacitor/core";

/** Origine du backend/relay utilisée par l'app native. */
export const NATIVE_RELAY_ORIGIN = "https://www.myserver.fr";

/** Vrai uniquement dans l'app Capacitor iOS/Android (jamais sur le web/PWA). */
export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Origine HTTP(S) pour les appels API et l'auth.
 * Natif → relay absolu ; web → origine courante (comportement inchangé).
 */
export function apiOrigin(): string {
  if (isNativeApp()) return NATIVE_RELAY_ORIGIN;
  return typeof window !== "undefined" ? window.location.origin : "http://localhost:8080";
}

/** Origine WebSocket (wss:// ou ws://) dérivée de apiOrigin().
 *
 * Override possible via `/config.json` → `relayWsUrl` (chargé au boot et mis
 * en cache dans `window.__remotews_relay_ws_url__`). Cible : permettre au
 * frontend servi par le backend Node de pointer le WebSocket vers un relai
 * distinct (typiquement le relai Rust sur un sous-domaine dédié) sans toucher
 * au code. La valeur de `relayWsUrl` doit être une URL absolue WS(S) sans
 * suffixe `/ws` ; on retire le suffixe au cas où.
 */
export function wsOrigin(): string {
  if (typeof window !== "undefined") {
    const override = (window as unknown as { __remotews_relay_ws_url__?: string }).__remotews_relay_ws_url__;
    if (typeof override === "string" && override.length > 0) {
      try {
        const u = new URL(override);
        // On garde uniquement l'origine ws(s)://host[:port] ; le path /ws sera
        // ajouté par les appelants (ws.tsx fait `${wsOrigin()}/ws`).
        const proto = u.protocol === "wss:" || u.protocol === "ws:" ? u.protocol : "wss:";
        return `${proto}//${u.host}`;
      } catch {
        /* override invalide : fallback */
      }
    }
  }
  return apiOrigin().replace(/^http/, "ws");
}

/**
 * Liste de relais candidats pour le failover (Lot C — LB multi-relai).
 *
 * Sources, dans l'ordre :
 *   1. `window.__remotews_relay_ws_url__` — primaire canonique.
 *   2. `window.__remotews_relay_ws_urls__` (array d'URLs absolues) — populée
 *      par `bootstrapConfig()` depuis `/config.json.relayWsUrls`.
 *   3. URL unique via `wsOrigin()` — comportement legacy single-relay.
 *
 * Le `WsProvider` itère sur la liste et bascule sur le suivant après N échecs
 * consécutifs sur le primaire. Si la liste contient 1 seul URL, comportement
 * identique au legacy.
 *
 * Chaque URL est normalisée : on garde origine + on ajoute `/ws` si absent
 * (les entrées `/config.json.relayWsUrls` peuvent être complètes ou pas).
 */
export function wsUrlList(): string[] {
  let raw: string[] = [];
  const addRaw = (url: string | undefined) => {
    const trimmed = typeof url === "string" ? url.trim() : "";
    if (trimmed && !raw.includes(trimmed)) raw.push(trimmed);
  };
  if (typeof window !== "undefined") {
    addRaw((window as unknown as { __remotews_relay_ws_url__?: string }).__remotews_relay_ws_url__);
    const arr = (window as unknown as { __remotews_relay_ws_urls__?: string[] }).__remotews_relay_ws_urls__;
    if (Array.isArray(arr) && arr.length > 0) {
      arr.forEach(addRaw);
    }
  }
  if (raw.length === 0) {
    raw = [`${wsOrigin()}/ws`];
  }
  const normalized: string[] = [];
  for (const u of raw) {
    try {
      const parsed = new URL(u);
      const proto = parsed.protocol === "wss:" || parsed.protocol === "ws:" ? parsed.protocol : "wss:";
      const path = parsed.pathname && parsed.pathname !== "/" ? parsed.pathname : "/ws";
      const next = `${proto}//${parsed.host}${path}`;
      if (!normalized.includes(next)) normalized.push(next);
    } catch {
      if (!normalized.includes(u)) normalized.push(u);
    }
  }
  return normalized;
}
